import { ADVICE_PORTAL_NEW, ADVICE_PORTAL_RECORDS } from "@/shared/ui/dock-pages";
import {
  isRecordFamilyKey,
  type PanelFamilyKey,
  type RecordFamilyKey,
  type RecordFamilyKind,
  type RecordSubjectKind,
} from "@/features/forms/shared/record/record-family";

export interface RecordLocation<K extends RecordFamilyKey = RecordFamilyKey> {
  family: K;
  recordId: string;
}

export interface RecordSubjectRef {
  kind: RecordSubjectKind;
  subjectId: string;
}

export interface RecordCopyNotice {
  copiedFrom: string | null;
  copiedCount: number | null;
  copyFailed: boolean;
  copiedFromKind?: RecordFamilyKind;
  copiedFromCreatedAt?: string;
  recreatedFrom?: string;
}

export interface RecordRouteState {
  clientLabel?: string | null;
  copy?: RecordCopyNotice;
  fromNewClient?: boolean;
}

export function recordPath(record: RecordLocation, step: string | null = null): string {
  const path = `${ADVICE_PORTAL_RECORDS}/${record.family}/${encodeURIComponent(record.recordId)}`;
  return step ? `${path}?${new URLSearchParams({ [STEP_PARAM]: step })}` : path;
}

export function startPath(
  subject: RecordSubjectRef | null = null,
  from: RecordLocation<PanelFamilyKey> | null = null,
): string {
  const query = new URLSearchParams();
  if (subject !== null) query.set(SUBJECT_PARAM, `${subject.kind}:${subject.subjectId}`);
  if (from !== null) query.set(FROM_PARAM, `${from.family}:${from.recordId}`);
  const search = query.toString();
  return search === "" ? ADVICE_PORTAL_NEW : `${ADVICE_PORTAL_NEW}?${search}`;
}

export function readRecordLocation(params: {
  family?: string;
  recordId?: string;
}): RecordLocation | null {
  const { family, recordId } = params;
  if (!isRecordFamilyKey(family) || !recordId) return null;
  return { family, recordId };
}

export function readRecordStep(search: URLSearchParams): string | null {
  return search.get(STEP_PARAM) || null;
}

export function readStartSubject(search: URLSearchParams): RecordSubjectRef | null {
  const raw = search.get(SUBJECT_PARAM) ?? "";
  const split = raw.indexOf(":");
  const kind = raw.slice(0, split);
  const subjectId = raw.slice(split + 1);
  if (split < 0 || subjectId === "") return null;
  if (kind !== "individual" && kind !== "corporation") return null;
  return { kind, subjectId };
}

export function readStartFrom(search: URLSearchParams): RecordLocation | null {
  const raw = search.get(FROM_PARAM) ?? "";
  const split = raw.indexOf(":");
  const family = raw.slice(0, split);
  const recordId = raw.slice(split + 1);
  if (split < 0 || recordId === "" || !isRecordFamilyKey(family)) return null;
  return { family, recordId };
}

export function readRecordRouteState(state: unknown): RecordRouteState {
  if (typeof state !== "object" || state === null) return {};
  const held = state as RecordRouteState;
  return {
    clientLabel: typeof held.clientLabel === "string" ? held.clientLabel : null,
    copy: held.copy,
    ...(held.fromNewClient === true ? { fromNewClient: true } : {}),
  };
}

export const PREVIEW_STEP = "preview";

const STEP_PARAM = "step";
const SUBJECT_PARAM = "subject";
const FROM_PARAM = "from";
